import React from "react";
import { ConfirmationModal } from "../Shared";
import { getLocalizedString } from "../../../shared/translation";

/**
 * Impact summary shown before saving a role-linkage change on a Data Access
 * Assignment (Requirements 4.7, 4.8, 4.9).
 *
 * Built on the shared `ConfirmationModal`. It states the count of affected
 * users and affected user groups derived from the role-linkage change. When
 * those counts are unavailable (the backend does not provide them — design
 * Open Question 3), it degrades to showing the count of linked roles with a
 * generic advisory instead.
 *
 * Confirm → `onConfirm` (the form sends the update); Cancel → `onCancel` (the
 * form stays open, retains values, and sends nothing).
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {number} [props.affectedUsers]      count of affected users, if known
 * @param {number} [props.affectedUserGroups] count of affected user groups, if known
 * @param {number} [props.linkedRoles]        linked-role count used for graceful degradation
 * @param {Function} props.onConfirm
 * @param {Function} props.onCancel
 */
export const RoleImpactSummaryModal = ({
  show,
  affectedUsers,
  affectedUserGroups,
  linkedRoles,
  onConfirm,
  onCancel,
}) => {
  // Counts are considered available only when provided as non-negative integers.
  const isCount = (value) => Number.isInteger(value) && value >= 0;
  const countsAvailable = isCount(affectedUsers) && isCount(affectedUserGroups);

  return (
    <ConfirmationModal
      show={show}
      onConfirm={onConfirm}
      onCancel={onCancel}
      yesLabel={getLocalizedString("confirm", "Confirm")}
      noLabel={getLocalizedString("cancel", "Cancel")}
      headerText={getLocalizedString("role_linkage_impact", "Role Linkage Impact")}
    >
      {countsAvailable ? (
        <div className="data-access-impact_summary text-center">
          <p>
            {getLocalizedString(
              "role_linkage_impact_intro",
              "Changing the linked roles will affect data access for:"
            )}
          </p>
          <p className="data-access-impact_counts">
            <strong>{affectedUsers}</strong>{" "}
            {getLocalizedString("affected_users", "affected users")}
            {" \u2022 "}
            <strong>{affectedUserGroups}</strong>{" "}
            {getLocalizedString("affected_user_groups", "affected user groups")}
          </p>
        </div>
      ) : (
        <div className="data-access-impact_summary text-center">
          <p className="data-access-impact_counts">
            <strong>{isCount(linkedRoles) ? linkedRoles : 0}</strong>{" "}
            {getLocalizedString("linked_roles", "linked role(s)")}
          </p>
          <p>
            {getLocalizedString(
              "role_linkage_impact_advisory",
              "Changing the linked roles may affect users and user groups that inherit access from these roles. Do you want to continue?"
            )}
          </p>
        </div>
      )}
    </ConfirmationModal>
  );
};
